import { Fragment, useCallback, useEffect, useMemo, useState } from 'react';
import { useAuth } from '@clerk/clerk-react';

import { createApiClient } from '../api.js';
import { UserSearchInput } from '../components/UserSearchInput.jsx';
import {
  DownloadIcon,
  ArrowRightIcon,
  XIcon,
  ChevronDownIcon,
} from '../components/icons';

/**
 * Audit log — every admin action, newest first, with filters.
 *
 * Backed by /v1/admin/audit (and actor/entity subroutes).  The chain
 * itself is hash-linked server-side; this page just presents the
 * trail in a digestible form.
 */

const ENTITY_TYPES = [
  { value: '', label: 'Any entity' },
  { value: 'tour', label: 'Tours' },
  { value: 'content', label: 'Content' },
  { value: 'checklist', label: 'Checklists' },
  { value: 'survey', label: 'Surveys' },
  { value: 'theme', label: 'Themes' },
  { value: 'project', label: 'Projects' },
  { value: 'site', label: 'Sites' },
  { value: 'webhook', label: 'Webhooks' },
  { value: 'api_key', label: 'API keys' },
  { value: 'team_member', label: 'Team members' },
  { value: 'invite', label: 'Invites' },
  { value: 'feature_flag', label: 'Feature flags' },
  { value: 'tenant_config', label: 'Tenant config' },
  { value: 'tenant', label: 'Tenants' },
];

const OPERATIONS = [
  { value: '', label: 'Any action' },
  { value: 'create', label: 'Create' },
  { value: 'update', label: 'Update' },
  { value: 'delete', label: 'Delete' },
  { value: 'publish', label: 'Publish' },
  { value: 'archive', label: 'Archive' },
  { value: 'rollback', label: 'Rollback' },
  { value: 'revoke', label: 'Revoke' },
  { value: 'remove', label: 'Remove' },
  { value: 'upsert', label: 'Upsert' },
  { value: 'set_default', label: 'Set default' },
  { value: 'copy', label: 'Copy' },
];

export function AuditLog() {
  const { getToken } = useAuth();
  const api = useMemo(() => createApiClient(getToken), [getToken]);

  const [entries, setEntries] = useState([]);
  const [nextCursor, setNextCursor] = useState(null);
  const [loading, setLoading] = useState(true);
  const [loadingMore, setLoadingMore] = useState(false);
  const [error, setError] = useState(null);
  const [expandedId, setExpandedId] = useState(null);

  const [filters, setFilters] = useState({
    actor: '',
    entity_type: '',
    operation: '',
    date_from: '',
    date_to: '',
  });

  const load = useCallback(async (cursor = null, currentFilters = filters) => {
    if (cursor) setLoadingMore(true); else setLoading(true);
    setError(null);
    try {
      const params = new URLSearchParams();
      if (cursor) params.set('cursor', cursor);
      if (currentFilters.entity_type) params.set('entity_type', currentFilters.entity_type);
      if (currentFilters.operation) params.set('operation', currentFilters.operation);
      if (currentFilters.date_from) params.set('date_from', currentFilters.date_from);
      if (currentFilters.date_to) params.set('date_to', currentFilters.date_to);

      // Actor filter uses the dedicated subroute so the server-side
      // GSI does the narrowing — cheaper than post-filtering.
      const path = currentFilters.actor
        ? `/v1/admin/audit/actor/${encodeURIComponent(currentFilters.actor)}`
        : '/v1/admin/audit';
      const qs = params.toString() ? `?${params.toString()}` : '';
      const resp = await api(`${path}${qs}`);
      const items = resp?.items || [];
      setEntries((prev) => cursor ? [...prev, ...items] : items);
      setNextCursor(resp?.next_cursor || null);
    } catch (e) {
      setError(e.message);
    } finally {
      setLoading(false);
      setLoadingMore(false);
    }
  }, [api, filters]);

  useEffect(() => { load(); }, [load]);

  const onFilter = (e) => {
    e.preventDefault();
    load(null, filters);
  };

  const onClear = () => {
    const empty = { actor: '', entity_type: '', operation: '', date_from: '', date_to: '' };
    setFilters(empty);
    load(null, empty);
  };

  const hasFilters = Object.values(filters).some(Boolean);

  const onExport = useCallback(async () => {
    // Pull every page that matches the current filters, then turn the
    // result into CSV.  Capped at 50 pages to avoid hammering Dynamo
    // if a tenant accumulates years of audit data — past that the
    // operator should use a direct DB export.
    let all = [];
    let cursor = null;
    let pages = 0;
    do {
      const params = new URLSearchParams();
      if (cursor) params.set('cursor', cursor);
      if (filters.entity_type) params.set('entity_type', filters.entity_type);
      if (filters.operation) params.set('operation', filters.operation);
      if (filters.date_from) params.set('date_from', filters.date_from);
      if (filters.date_to) params.set('date_to', filters.date_to);
      const path = filters.actor
        ? `/v1/admin/audit/actor/${encodeURIComponent(filters.actor)}`
        : '/v1/admin/audit';
      const qs = params.toString() ? `?${params.toString()}` : '';
      const resp = await api(`${path}${qs}`);
      const items = resp?.items || [];
      all = all.concat(items);
      cursor = resp?.next_cursor || null;
      pages += 1;
    } while (cursor && pages < 50);

    const csv = toCsv(all);
    const blob = new Blob([csv], { type: 'text/csv;charset=utf-8' });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    const stamp = new Date().toISOString().replace(/[:.]/g, '-').slice(0, 19);
    a.href = url;
    a.download = `audit-${stamp}.csv`;
    document.body.appendChild(a);
    a.click();
    document.body.removeChild(a);
    URL.revokeObjectURL(url);
  }, [api, filters]);

  return (
    <div className="page">
      <div className="page__title">
        <div>
          <h1>Audit log</h1>
          <p className="page__subtitle">
            Every admin action, immutable, linked to the previous one by
            a SHA-256 hash chain. Tampering with a single row invalidates
            every row after it. Verify the chain with{' '}
            <code>python scripts/verify_audit_chain.py</code>.
          </p>
        </div>
        <div>
          <button
            type="button"
            className="btn btn--secondary"
            onClick={onExport}
            disabled={loading}
            style={{ whiteSpace: 'nowrap' }}
          >
            <DownloadIcon /> Export CSV
          </button>
        </div>
      </div>

      <form className="audit-filters card" onSubmit={onFilter}>
        <div className="audit-filters__field audit-filters__field--user">
          <span>Actor</span>
          <UserSearchInput
            value={filters.actor}
            onChange={(uid) => setFilters({ ...filters, actor: uid })}
            api={api}
            placeholder="Search by name, email, or id…"
          />
        </div>
        <label className="audit-filters__field">
          <span>Entity</span>
          <select
            value={filters.entity_type}
            onChange={(e) => setFilters({ ...filters, entity_type: e.target.value })}
          >
            {ENTITY_TYPES.map((t) => (
              <option key={t.value} value={t.value}>{t.label}</option>
            ))}
          </select>
        </label>
        <label className="audit-filters__field">
          <span>Action</span>
          <select
            value={filters.operation}
            onChange={(e) => setFilters({ ...filters, operation: e.target.value })}
          >
            {OPERATIONS.map((o) => (
              <option key={o.value} value={o.value}>{o.label}</option>
            ))}
          </select>
        </label>
        <label className="audit-filters__field">
          <span>From</span>
          <input
            type="date"
            value={filters.date_from}
            onChange={(e) => setFilters({ ...filters, date_from: e.target.value })}
          />
        </label>
        <label className="audit-filters__field">
          <span>To</span>
          <input
            type="date"
            value={filters.date_to}
            onChange={(e) => setFilters({ ...filters, date_to: e.target.value })}
          />
        </label>
        <div className="audit-filters__actions">
          <button type="submit" className="btn btn--primary">
            <ArrowRightIcon /> Apply
          </button>
          {hasFilters && (
            <button type="button" className="btn btn--secondary" onClick={onClear}>
              <XIcon /> Clear
            </button>
          )}
        </div>
      </form>

      {loading && <div className="state"><div className="state__title">Loading audit log…</div></div>}
      {error && !loading && (
        <div className="state state--error">
          <div className="state__title">Couldn't load audit log</div>
          <div>{error}</div>
        </div>
      )}

      {!loading && !error && (
        entries.length === 0 ? (
          <div className="state">
            <div className="state__title">No entries match</div>
            <div>
              {hasFilters
                ? 'Loosen the filters above, or clear them to see the full log.'
                : "Admin actions (create/update/delete tours, content, sites, webhooks, keys, etc.) will show up here as they happen."}
            </div>
          </div>
        ) : (
          <>
            <div className="card" style={{ padding: 0, overflow: 'hidden' }}>
              <table className="audit-table">
                <thead>
                  <tr>
                    <th>When</th>
                    <th>Actor</th>
                    <th>What happened</th>
                    <th>Reference</th>
                  </tr>
                </thead>
                <tbody>
                  {entries.map((entry) => {
                    const id = entry.event_id || `${entry.timestamp}-${entry.actor_id}`;
                    const expanded = expandedId === id;
                    const hasDetail =
                      (entry.before && Object.keys(entry.before).length > 0) ||
                      (entry.after && Object.keys(entry.after).length > 0);
                    return (
                      <Fragment key={id}>
                        <tr
                          className={hasDetail ? 'audit-row audit-row--clickable' : 'audit-row'}
                          onClick={() => hasDetail && setExpandedId(expanded ? null : id)}
                        >
                          <td className="audit-table__when">
                            <div className="audit-when">
                              <span className="audit-when__relative">{formatRelative(entry.timestamp)}</span>
                              <span
                                className="audit-when__absolute"
                                title={formatAbsoluteFull(entry.timestamp)}
                              >
                                {formatAbsoluteShort(entry.timestamp)}
                              </span>
                            </div>
                          </td>
                          <td>
                            {entry.actor_name ? (
                              <span title={entry.actor_id}>
                                <strong>{entry.actor_name}</strong>
                                {entry.actor_email && (
                                  <span style={{ color: 'var(--text-3)', fontSize: 11.5, display: 'block' }}>
                                    {entry.actor_email}
                                  </span>
                                )}
                              </span>
                            ) : (
                              <span title={entry.actor_id}>
                                {entry.actor_id || <span style={{ color: 'var(--text-3)' }}>system</span>}
                              </span>
                            )}
                            {entry.api_key_prefix && (
                              <div style={{ color: 'var(--text-3)', fontSize: 11, marginTop: 2 }}>
                                via key <code>{entry.api_key_prefix}…</code>
                              </div>
                            )}
                          </td>
                          <td>
                            <span className={`badge ${operationBadge(entry.operation)}`}>
                              {describeAction(entry)}
                            </span>
                          </td>
                          <td>
                            <code className="audit-table__ref">
                              {entry.entity_type}{entry.entity_id ? `/${entry.entity_id}` : ''}
                            </code>
                          </td>
                        </tr>
                        {expanded && hasDetail && (
                          <tr className="audit-row__detail">
                            <td colSpan={4}>
                              <div className="audit-detail">
                                {entry.before && Object.keys(entry.before).length > 0 && (
                                  <div>
                                    <div className="audit-detail__label">Before</div>
                                    <pre>{JSON.stringify(entry.before, null, 2)}</pre>
                                  </div>
                                )}
                                {entry.after && Object.keys(entry.after).length > 0 && (
                                  <div>
                                    <div className="audit-detail__label">After</div>
                                    <pre>{JSON.stringify(entry.after, null, 2)}</pre>
                                  </div>
                                )}
                              </div>
                            </td>
                          </tr>
                        )}
                      </Fragment>
                    );
                  })}
                </tbody>
              </table>
            </div>
            {nextCursor && (
              <div style={{ textAlign: 'center', marginTop: 12 }}>
                <button
                  type="button"
                  className="btn btn--secondary"
                  disabled={loadingMore}
                  onClick={() => load(nextCursor)}
                >
                  <ChevronDownIcon /> {loadingMore ? 'Loading…' : 'Load older'}
                </button>
              </div>
            )}
          </>
        )
      )}
      <style>{AUDIT_CSS}</style>
    </div>
  );
}

function formatTime(iso) {
  try {
    const d = new Date(iso);
    return d.toLocaleString(undefined, {
      year: 'numeric', month: 'short', day: 'numeric',
      hour: '2-digit', minute: '2-digit',
    });
  } catch { return iso; }
}

/** "5m ago", "2h ago", "Yesterday", "Mar 12" — shorter for closer events. */
function formatRelative(iso) {
  try {
    const d = new Date(iso);
    const diffMs = Date.now() - d.getTime();
    const sec = Math.round(diffMs / 1000);
    if (sec < 5) return 'just now';
    if (sec < 60) return `${sec}s ago`;
    const min = Math.round(sec / 60);
    if (min < 60) return `${min}m ago`;
    const hr = Math.round(min / 60);
    if (hr < 24) return `${hr}h ago`;
    const day = Math.round(hr / 24);
    if (day === 1) return 'yesterday';
    if (day < 7) return `${day}d ago`;
    const wk = Math.round(day / 7);
    if (wk < 5) return `${wk}w ago`;
    const mo = Math.round(day / 30);
    if (mo < 12) return `${mo}mo ago`;
    const yr = Math.round(day / 365);
    return `${yr}y ago`;
  } catch { return ''; }
}

/** "12 May 14:32" — compact absolute. */
function formatAbsoluteShort(iso) {
  try {
    const d = new Date(iso);
    return d.toLocaleString(undefined, {
      month: 'short', day: 'numeric',
      hour: '2-digit', minute: '2-digit',
    });
  } catch { return iso; }
}

/** Tooltip: full date + time + seconds for verification work. */
function formatAbsoluteFull(iso) {
  try {
    const d = new Date(iso);
    return d.toLocaleString(undefined, {
      year: 'numeric', month: 'long', day: 'numeric',
      hour: '2-digit', minute: '2-digit', second: '2-digit',
      timeZoneName: 'short',
    });
  } catch { return iso; }
}

/** Flatten audit rows into CSV for export.  Before/after JSON-encoded. */
function toCsv(rows) {
  const headers = [
    'timestamp', 'actor_id', 'entity_type', 'entity_id',
    'operation', 'event_type', 'before', 'after', 'event_id',
  ];
  const escape = (v) => {
    if (v == null) return '';
    const s = typeof v === 'string' ? v : JSON.stringify(v);
    if (/[",\n]/.test(s)) return `"${s.replace(/"/g, '""')}"`;
    return s;
  };
  const lines = [headers.join(',')];
  for (const r of rows) {
    lines.push(headers.map((h) => escape(r[h])).join(','));
  }
  return lines.join('\n');
}

// Map operation -> human verb used in "<Verb> <entity> <name>" rendering.
const VERBS = {
  create: 'Created',
  update: 'Updated',
  delete: 'Deleted',
  publish: 'Published',
  archive: 'Archived',
  rollback: 'Rolled back',
  revoke: 'Revoked',
  remove: 'Removed',
  upsert: 'Saved',
  set_default: 'Made default',
  copy: 'Copied',
};

const ENTITY_LABELS = {
  tour: 'tour',
  content: 'content item',
  checklist: 'checklist',
  survey: 'survey',
  theme: 'theme',
  project: 'project',
  site: 'site',
  webhook: 'webhook',
  api_key: 'API key',
  team_member: 'team member',
  invite: 'invite',
  feature_flag: 'feature flag',
  tenant_config: 'tenant config',
  tenant: 'tenant',
  admin_user: 'admin user',
};

function describeAction(entry) {
  const verb = VERBS[(entry.operation || '').toLowerCase()]
    || (entry.operation ? titleCase(entry.operation) : 'Action');
  const label = ENTITY_LABELS[entry.entity_type] || entry.entity_type || 'entity';
  const snapshot = entry.after || entry.before || {};
  const name = snapshot.name || snapshot.title || snapshot.slug || '';
  return name ? `${verb} ${label} “${name}”` : `${verb} ${label}`;
}

function titleCase(s) {
  return s.charAt(0).toUpperCase() + s.slice(1).toLowerCase().replace(/_/g, ' ');
}

function operationBadge(op) {
  const normalised = (op || '').toLowerCase();
  if (normalised.includes('delete') || normalised.includes('revoke') || normalised.includes('remove')) {
    return 'badge--default';
  }
  if (normalised.includes('create') || normalised.includes('publish') || normalised.includes('copy')) {
    return 'badge--editor';
  }
  if (normalised.includes('update') || normalised.includes('patch') || normalised.includes('upsert')) {
    return 'badge--admin';
  }
  return 'badge--viewer';
}

const AUDIT_CSS = `
  .audit-filters {
    display: flex;
    flex-wrap: wrap;
    gap: 12px;
    padding: 14px 16px;
    margin-bottom: 16px;
    align-items: flex-end;
  }
  .audit-filters__field {
    display: flex;
    flex-direction: column;
    gap: 4px;
    font-size: 12px;
    color: var(--text-3);
  }
  .audit-filters__field span {
    text-transform: uppercase;
    letter-spacing: 0.04em;
    font-size: 11px;
  }
  .audit-filters__field input,
  .audit-filters__field select {
    padding: 7px 9px;
    border: 1px solid var(--border-1);
    border-radius: 6px;
    background: var(--surface-0);
    color: var(--text-1);
    font-size: 13px;
    min-width: 150px;
  }
  /* Actor cell — the UserSearchInput wants a bit more width so the
     dropdown rows don't truncate the email. */
  .audit-filters__field--user { min-width: 240px; }
  .audit-filters__field--user .user-search { width: 100%; }
  .audit-filters__actions {
    display: flex;
    gap: 8px;
    margin-left: auto;
  }
  .audit-table {
    width: 100%;
    border-collapse: collapse;
    font-size: 13px;
  }
  .audit-table th,
  .audit-table td {
    padding: 10px 14px;
    text-align: left;
    border-bottom: 1px solid var(--border-1);
  }
  .audit-table th {
    font-weight: 600;
    font-size: 11.5px;
    text-transform: uppercase;
    letter-spacing: 0.04em;
    color: var(--text-3);
    background: var(--surface-1);
  }
  .audit-table tbody tr:last-child td {
    border-bottom: none;
  }
  .audit-table__when {
    white-space: nowrap;
    color: var(--text-3);
    font-variant-numeric: tabular-nums;
    vertical-align: top;
    padding-top: 12px;
  }
  .audit-when {
    display: flex;
    flex-direction: column;
    gap: 2px;
    line-height: 1.25;
  }
  .audit-when__relative {
    font-weight: 500;
    color: var(--text-1);
    font-size: 12.5px;
  }
  .audit-when__absolute {
    color: var(--text-3);
    font-size: 11px;
    cursor: help;
    text-decoration: underline dotted;
    text-underline-offset: 2px;
  }
  .audit-table__ref {
    font-size: 12px;
    color: var(--text-3);
  }
  .audit-row--clickable { cursor: pointer; }
  .audit-row--clickable:hover { background: var(--surface-1); }
  .audit-row__detail td { background: var(--surface-1); padding: 0; }
  .audit-detail {
    display: grid;
    grid-template-columns: 1fr 1fr;
    gap: 16px;
    padding: 12px 14px;
  }
  .audit-detail__label {
    text-transform: uppercase;
    font-size: 10.5px;
    letter-spacing: 0.04em;
    color: var(--text-3);
    margin-bottom: 4px;
  }
  .audit-detail pre {
    background: var(--surface-0);
    padding: 10px;
    border-radius: 6px;
    font-size: 12px;
    overflow: auto;
    max-height: 240px;
    margin: 0;
  }
`;
